// src/router/index.js

import { createRouter, createWebHistory } from 'vue-router'
import { doc, getDoc } from 'firebase/firestore'
import { db } from '@/services/firebaseconfig'
import { eventBus } from '@/utils/eventBus'
import { homePathForRoles } from '@/constants/roleRedirect'
import { useUserStore } from '@/stores/userStore'
import { usePermissionsStore } from '@/stores/permissionsStore'
import { useMaintenanceStore } from '@/stores/maintenanceStore'
import { maybeRefreshOnNavigation, recoverFromMissingChunk } from '@/services/backgroundRefresh'
import { isChunkLoadError } from '@/utils/chunkLoadRecovery'
import { useFrontendFlagsStore } from '@/stores/frontendFlagsStore'
import { useCallStore } from '@/stores/callStore'
import { isCustomerOwner } from '@/utils/customerOwnership'

// All view imports are lazy-loaded for code splitting

export const routes = [
  // --- PUBLIC ---
  {
    path: '/sign-in',
    name: 'SignIn',
    component: () => import('@/views/SignInPage.vue'),
    meta: { public: true },
  },
  {
    path: '/forgot-password',
    name: 'ForgotPassword',
    component: () => import('@/views/ForgotPasswordPage.vue'),
    meta: { public: true },
  },
  {
    path: '/reset-password',
    name: 'ConsumerResetPassword',
    component: () => import('@/views/ConsumerResetPassword.vue'),
    meta: { public: true },
  },
  {
    path: '/signed-out',
    name: 'SignedOut',
    component: () => import('@/views/SignedOutPage.vue'),
    meta: { public: true },
  },
  {
    path: '/',
    name: 'ConsumerHome',
    component: () => import('@/views/ConsumerHomePage.vue'),
    meta: { public: true },
  },
  {
    path: '/complete-invitation',
    name: 'CompleteInvitation',
    component: () => import('@/views/CompleteInvitationPage.vue'),
    meta: { public: true },
  },
  {
    path: '/privacy-policy',
    name: 'ConsumerPrivacyPolicy',
    component: () => import('@/views/ConsumerPrivacyPolicy.vue'),
    meta: { public: true },
  },
  {
    path: '/terms-of-service',
    name: 'ConsumerTermsOfService',
    component: () => import('@/views/ConsumerTermsOfService.vue'),
    meta: { public: true },
  },
  {
    path: '/about-us',
    name: 'ConsumerAboutUs',
    component: () => import('@/views/ConsumerAboutUs.vue'),
    meta: { public: true },
  },
  {
    path: '/contact-us',
    name: 'ConsumerContact',
    component: () => import('@/views/ConsumerContact.vue'),
    meta: { public: true },
  },
  // Two blog posts linked /contact, which had never existed (Erik 2026-11-04); a blog post may link only to the pages in
  // functions-shared/marketing/blogLinks.js, and this redirect keeps any old /contact link working.
  { path: '/contact', redirect: '/contact-us' },
  {
    path: '/testimonials',
    name: 'ConsumerTestimonials',
    component: () => import('@/views/ConsumerTestimonials.vue'),
    meta: { public: true },
  },
  {
    // How it works: the customer's path to solar (docs/features/consumer_how_it_works_FEATURE.md). Public by
    // design, like /faqs. Behind frontendFlags.howItWorksPage: while it is off the page sends visitors home,
    // and the header shows no link to it.
    path: '/how-it-works',
    name: 'ConsumerHowItWorks',
    component: () => import('@/views/ConsumerHowItWorks.vue'),
    meta: { public: true },
    beforeEnter: async () => {
      const flagsStore = useFrontendFlagsStore()
      await flagsStore.whenLoaded()
      if (!flagsStore.isFlagOn('howItWorksPage', false)) return { name: 'ConsumerHome' }
    },
  },
  {
    path: '/faqs',
    name: 'ConsumerFAQs',
    component: () => import('@/views/ConsumerFAQs.vue'),
    meta: { public: true },
  },
  {
    path: '/blog',
    name: 'ConsumerBlog',
    component: () => import('@/views/ConsumerBlog.vue'),
    meta: { public: true },
  },
  {
    path: '/blog/:id',
    name: 'BlogDetail',
    component: () => import('@/views/ConsumerBlogDetail.vue'),
    meta: { public: true },
  },
  {
    // Where a scanned direct-mail QR lands. PUBLIC by design: the person
    // holding the letter has no account. The ?t= token is matched against a
    // stored hash server-side; this route never sees an address.
    path: '/ilsfa/start',
    name: 'IlsfaStart',
    component: () => import('@/views/IlsfaStartPage.vue'),
    meta: { public: true },
  },
  {
    path: '/savings-calculator',
    name: 'SavingsCalculator',
    component: () => import('@/views/SavingsCalculatorPage.vue'),
    meta: { public: true },
  },
  {
    path: '/schedule-call',
    name: 'ScheduleCall',
    component: () => import('@/views/ScheduleCallPage.vue'),
    meta: { public: true },
  },
  {
    path: '/thank-you',
    name: 'ConsumerThankYou',
    component: () => import('@/views/ConsumerThankYou.vue'),
    meta: { public: true },
  },
  // Phase 66A — CAN-SPAM unsubscribe landing
  {
    path: '/unsubscribe',
    name: 'Unsubscribe',
    component: () => import('@/views/UnsubscribePage.vue'),
    meta: { public: true },
  },
  { path: '/log-in', redirect: '/sign-in' },
  // Internal portal pages (moved to different paths)
  {
    path: '/portal/about',
    name: 'AboutUs',
    component: () => import('@/views/AboutUs.vue'),
    meta: { public: true },
  },
  {
    path: '/portal/contact',
    name: 'ContactUs',
    component: () => import('@/views/ContactUs.vue'),
    meta: { public: true },
  },
  {
    path: '/portal/privacy',
    name: 'PrivacyPolicy',
    component: () => import('@/views/PrivacyPolicy.vue'),
    meta: { public: true },
  },
  {
    path: '/portal/terms',
    name: 'TermsOfService',
    component: () => import('@/views/TermsOfService.vue'),
    meta: { public: true },
  },
  {
    path: '/maintenance',
    name: 'Maintenance',
    component: () => import('@/views/MaintenancePage.vue'),
    meta: { public: true },
  },
  {
    path: '/unauthorized',
    name: 'Unauthorized',
    component: () => import('@/views/UnauthorizedPage.vue'),
    meta: { public: true, systemControlled: true },
  },

  // --- MAIN NAVIGATION ---
  {
    path: '/home',
    name: 'UserHome',
    component: () => import('@/views/UserHomePage.vue'),
    // No navLabel — this route is accessed via the "My Page" link in the user dropdown, not the top nav
    meta: { requiresAuth: true, module: 'home' },
  },
  {
    path: '/expenses',
    name: 'ExpenseReports',
    component: () => import('@/views/ExpenseReportsPage.vue'),
    // No navLabel — accessed via the "Expense Reports" link in the user dropdown.
    // Module-flagged so the page can be hidden via globalSettings/frontendFlags.
    meta: { requiresAuth: true, module: 'expenses' },
  },
  {
    // Email-alert deep-link resolver (K3P0DV7j): alert emails link here with
    // the alert's flat fields; the page resolves through the shared routing
    // map and replaces. No navLabel — never navigated to by hand.
    path: '/alerts/open',
    name: 'AlertOpenRedirect',
    component: () => import('@/views/AlertOpenRedirect.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/dashboard',
    name: 'Dashboard',
    component: () => import('@/views/DashboardPage.vue'),
    meta: {
      requiresAuth: true,
      resourceKey: 'Dashboard',
      navLabel: 'Dashboard',
    },
  },
  {
    // Phase 97 — the Command Board (docs/features/PHASE97_COMMAND_BOARD_FEATURE.md). One page, four sections, each
    // its own key so a section's audience is decided on its own (SECURITY_RULES 2.2.1). The page redirects a bare
    // /command-board to the first section the user can open. Dark behind commandBoard_enabled (seeded false).
    path: '/command-board',
    name: 'CommandBoard',
    component: () => import('@/views/CommandBoardPage.vue'),
    meta: {
      requiresAuth: true,
      resourceKey: 'CommandBoard',
      navLabel: 'Command Board',
      dashboardGroup: true,
      module: 'commandBoard_enabled',
    },
    children: [
      {
        path: 'leads',
        name: 'CommandBoardLeads',
        component: () => import('@/components/CommandBoard/sections/LeadsSection.vue'),
        meta: {
          requiresAuth: true,
          resourceKey: 'CommandBoardLeads',
          module: 'commandBoard_enabled',
        },
      },
      {
        path: 'customers',
        name: 'CommandBoardCustomers',
        component: () => import('@/components/CommandBoard/sections/CustomersSection.vue'),
        meta: {
          requiresAuth: true,
          resourceKey: 'CommandBoardCustomers',
          module: 'commandBoard_enabled',
        },
      },
      {
        path: 'operations',
        name: 'CommandBoardOperations',
        component: () => import('@/components/CommandBoard/sections/OperationsSection.vue'),
        meta: {
          requiresAuth: true,
          resourceKey: 'CommandBoardOperations',
          module: 'commandBoard_enabled',
        },
      },
      {
        path: 'maintenance',
        name: 'CommandBoardMaintenance',
        component: () => import('@/components/CommandBoard/sections/MaintenanceSection.vue'),
        meta: {
          requiresAuth: true,
          resourceKey: 'CommandBoardMaintenance',
          module: 'commandBoard_enabled',
        },
      },
    ],
  },
  {
    path: '/leads',
    name: 'LeadDashboard',
    component: () => import('@/views/LeadDashboardPage.vue'),
    meta: {
      requiresAuth: true,
      resourceKey: 'LeadDashboard',
      navLabel: 'Leads',
      dashboardGroup: true,
      module: 'leadManagement',
    },
  },
  {
    path: '/archive',
    name: 'ArchiveBrowser',
    component: () => import('@/views/ArchiveBrowserPage.vue'),
    meta: {
      requiresAuth: true,
      resourceKey: 'ArchiveBrowser',
      navLabel: 'Finished',
      dashboardGroup: true,
    },
  },
  {
    path: '/overview',
    name: 'CompanyOverview',
    component: () => import('@/views/CompanyOverview.vue'),
    meta: {
      requiresAuth: true,
      resourceKey: 'CompanyOverview',
      navLabel: 'Company Overview',
      inMoreMenu: true,
      category: 'Quick Access',
    },
  },
  // #34 — CreatePage retired + DELETED (2026-07-13): the Solar Studio (/studio, below) is the sole
  // create/estimator surface. The '/create' route + CreatePage.vue + FinancialEstimateCard.vue are gone;
  // the shared 'Create' permission key lives on /studio.
  // Phase 90 Design Studio — the staged Customer→Design→Estimate tool that REPLACES the Create Page +
  // Aurora. Promoted to the primary create surface at #34 (flag-gate removed — it is now core, not a
  // dark feature; carries the 'Create' navLabel + the shared 'Create' permission key).
  {
    path: '/studio',
    name: 'SolarStudio',
    component: () => import('@/views/SolarStudioPage.vue'),
    meta: {
      requiresAuth: true,
      resourceKey: 'Create',
      navLabel: 'Design Studio',
      createGroup: true,
    },
  },
  {
    path: '/pricing-lab',
    name: 'PricingScenarioBuilder',
    component: () => import('@/views/PricingScenarioBuilderPage.vue'),
    meta: {
      requiresAuth: true,
      resourceKey: 'PricingScenarioBuilder',
      navLabel: 'Pricing Lab',
      createGroup: true,
      module: 'pricingScenarioBuilder',
    },
  },
  {
    path: '/kiosk',
    name: 'Kiosk',
    component: () => import('@/views/KioskCreatePage.vue'),
    meta: {
      public: true, // No authentication required
      hideNav: true, // Optional: hide main navigation for kiosk mode
    },
  },

  {
    path: '/docs-procedures',
    name: 'DocsProcedures',
    component: () => import('@/views/DocsProceduresPage.vue'),
    meta: {
      requiresAuth: true,
      resourceKey: 'DocsProcedures',
      navLabel: 'Docs & Procedures',
      inMoreMenu: true,
      category: 'Quick Access',
      module: 'docsProcedures',
    },
  },

  // --- BUG REPORTS ---
  {
    path: '/bug-reports',
    name: 'BugReports',
    component: () => import('@/views/BugReportsPage.vue'),
    meta: {
      requiresAuth: true,
      module: 'bugReports',
    },
  },

  // --- CUSTOMER SPECIFIC ---
  {
    path: '/customers/:id',
    name: 'CustomerCommandCenter',
    component: () => import('@/views/CustomerCommandCenter.vue'),
    props: true,
    meta: {
      requiresAuth: true,
      resourceKey: 'CustomerDetail',
    },
  },
  // --- CUSTOMER PORTAL ---
  // Customer self-service portal (uses logged-in user's linked customer ID)
  {
    path: '/portal',
    name: 'CustomerPortal',
    component: () => import('@/views/CustomerPortal.vue'),
    meta: {
      requiresAuth: true,
      resourceKey: 'CustomerPortal',
      module: 'portal',
    },
  },
  // Admin preview of customer portal (with specific customer ID)
  {
    path: '/portal/tracker/:eventId',
    name: 'PortalTechnicianTracker',
    component: () => import('@/views/PortalTechnicianTrackerPage.vue'),
    props: true,
    meta: { requiresAuth: true, resourceKey: 'CustomerPortal', module: 'portal' },
  },
  {
    path: '/portal/:customerId',
    name: 'CustomerPortalPreview',
    component: () => import('@/views/CustomerPortal.vue'),
    props: true,
    meta: {
      requiresAuth: true,
      resourceKey: 'CustomerPortalPreview',
      module: 'portal',
    },
  },
  // Customer portal authentication (public)
  {
    path: '/portal/sign-in',
    name: 'CustomerSignIn',
    component: () => import('@/views/CustomerSignInPage.vue'),
    meta: { public: true },
  },
  // Consumer-styled account activation — the single invite-link destination for
  // BOTH customers and staff (Erik ruling 2026-07-21: internal users on a
  // consumer-styled page beats customers on an internal-styled one). The page
  // redirects by account type after activation: customers → /portal, staff →
  // /studio. `/activate` is the neutral alias used for staff invites so their
  // link doesn't read as "portal".
  {
    path: '/portal/activate',
    alias: '/activate',
    name: 'CustomerActivate',
    component: () => import('@/views/CustomerActivatePage.vue'),
    meta: { public: true },
  },
  // Phase 62 — Public signing page. The token in the URL is the auth gate
  // for external signers; portal customers + GSE staff also use this same
  // route (their identity is recorded via Firebase Auth uid in the actor).
  {
    path: '/sign/:token',
    name: 'SignDocument',
    component: () => import('@/views/SignDocumentPage.vue'),
    props: true,
    meta: { public: true },
  },
  // The portal customer's forgot-password page — consumer-styled, returns to
  // /portal/sign-in. Staff keep /forgot-password
  // (docs/bugs/portal_forgot_password_staff_page_BUG.md).
  {
    path: '/portal/forgot-password',
    name: 'CustomerForgotPassword',
    component: () => import('@/views/CustomerForgotPasswordPage.vue'),
    meta: { public: true },
  },
  {
    path: '/portal/register',
    name: 'CustomerRegister',
    component: () => import('@/views/CustomerRegistrationPage.vue'),
    meta: { public: true },
  },
  // Live monitoring dashboard is STAFF-ONLY (Erik ruling 2026-07-18, Phase 91):
  // customers get curated monitoring data on the portal landing, never this UI.
  // The former customer route /portal/monitoring/:siteId was removed; only the
  // admin deep-link below remains.
  // Admin deep-link to live monitoring
  {
    path: '/monitoring/live/:siteId',
    name: 'MonitoringDashboardAdmin',
    component: () => import('@/views/MonitoringDashboard.vue'),
    props: true,
    meta: {
      requiresAuth: true,
      resourceKey: 'SiteMonitoring',
    },
  },

  // --- MORE MENU: TOOLS ---
  {
    path: '/fleet',
    name: 'FleetManagement',
    component: () => import('@/views/FleetManagementPage.vue'),
    meta: {
      requiresAuth: true,
      // ⚠️ Its own key, NOT a borrowed one. SECURITY_RULES §2.2.1: a reused key
      // inherits the other surface's policy changes forever, and this screen's
      // audience (office coordination) is deliberately different from
      // SiteMonitoring's (which includes field crew).
      resourceKey: 'FleetManagement',
      navLabel: 'Fleet',
      inMoreMenu: true,
      category: 'Operations',
      // Spec §11 frontend flag. `isModuleEnabled` returns `flags[x] !== false`,
      // and fleet_ui_enabled is seeded false in the store DEFAULTS — so this is
      // fail-CLOSED: the route bounces and the More-menu entry hides until the
      // flag is turned on, even if Firestore is unreachable.
      module: 'fleet_ui_enabled',
    },
  },
  {
    path: '/monitoring',
    name: 'SiteMonitoring',
    component: () => import('@/views/SiteMonitoringPage.vue'),
    meta: {
      requiresAuth: true,
      resourceKey: 'SiteMonitoring',
      navLabel: 'Site Monitoring',
      inMoreMenu: true,
      category: 'Operations',
    },
  },
  {
    // M-RETs filing as its own page (docs/features/mrets_filing_page_FEATURE.md). Reuses `MretsSubmission`, the key the
    // Site Monitoring M-RETS button already checks, whose meaning is unchanged here: "works M-RETs filing"
    // (SECURITY_RULES §2.2.1, reuse checked in the spec's Access section). Not in a menu: the button opens it.
    // `module: 'mretsFilingPage'` is seeded false in the flag store's DEFAULTS, so the route is fail-closed until on.
    path: '/monitoring/mrets',
    name: 'MretsFiling',
    component: () => import('@/views/MretsFilingPage.vue'),
    meta: {
      requiresAuth: true,
      resourceKey: 'MretsSubmission',
      module: 'mretsFilingPage',
    },
  },
  {
    path: '/calendar',
    name: 'CalendarPage',
    component: () => import('@/views/CalendarPage.vue'),
    meta: {
      requiresAuth: true,
      resourceKey: 'Calendar',
      navLabel: 'Calendar',
      inMoreMenu: true,
      category: 'Quick Access',
    },
  },
  {
    path: '/service-cases',
    name: 'ServiceCases',
    component: () => import('@/views/ServiceCasesPage.vue'),
    meta: {
      requiresAuth: true,
      resourceKey: 'ServiceCases',
      navLabel: 'Service Cases',
      inMoreMenu: true,
      category: 'Operations',
      module: 'serviceCases',
    },
  },
  {
    path: '/kitting',
    name: 'KittingSchedule',
    component: () => import('@/views/KittingSchedulePage.vue'),
    meta: {
      requiresAuth: true,
      resourceKey: 'KittingSchedule',
      navLabel: 'Kitting',
      inMoreMenu: true,
      category: 'Operations',
    },
  },
  {
    path: '/communications',
    name: 'Communications',
    component: () => import('@/views/CommunicationsPage.vue'),
    meta: {
      requiresAuth: true,
      resourceKey: 'Communications',
      navLabel: 'Communications',
      inMoreMenu: true,
      category: 'Quick Access',
      module: 'communications',
    },
  },
  {
    path: '/lead-pool',
    name: 'LeadPool',
    component: () => import('@/views/LeadPoolPage.vue'),
    meta: {
      requiresAuth: true,
      resourceKey: 'LeadPool',
      navLabel: 'Lead Pool',
      inMoreMenu: true,
      category: 'Pools',
      module: 'leadManagement',
    },
  },
  {
    // Ready for Install board (replaced the Install Lead Pool 2026-09-14). The route name and access key stay
    // `InstallLeadPool`: renaming the key would force an access-matrix change for no user benefit.
    path: '/ready-for-install',
    name: 'InstallLeadPool',
    component: () => import('@/views/ReadyForInstallPage.vue'),
    meta: {
      requiresAuth: true,
      resourceKey: 'InstallLeadPool',
      navLabel: 'Ready for Install',
      inMoreMenu: true,
      category: 'Pools',
      module: 'leadManagement',
    },
  },
  { path: '/install-lead-pool', redirect: { name: 'InstallLeadPool' } },
  // --- MORE MENU: FINANCE ---
  {
    path: '/payments',
    name: 'PaymentsPage',
    component: () => import('@/views/PaymentsPage.vue'),
    meta: {
      requiresAuth: true,
      resourceKey: 'Payments',
      navLabel: 'Payments',
      inMoreMenu: true,
      category: 'Finance',
      module: 'finance',
    },
  },
  {
    path: '/admin-finance',
    name: 'AdminFinance',
    component: () => import('@/views/AdminFinancePage.vue'),
    meta: {
      requiresAuth: true,
      resourceKey: 'AdminFinance',
      navLabel: 'Admin Finance',
      inMoreMenu: true,
      category: 'Finance',
      module: 'finance',
    },
  },

  // --- MORE MENU: ADMINISTRATION ---
  {
    path: '/admin/user-management',
    name: 'UserManagement',
    component: () => import('@/views/UserManagementPage.vue'),
    meta: {
      requiresAuth: true,
      resourceKey: 'UserManagement',
      navLabel: 'Users & Orgs',
      inMoreMenu: true,
      category: 'People',
    },
  },
  {
    path: '/admin/organizations',
    name: 'AdminOrgOverview',
    component: () => import('@/views/admin/AdminOrgOverview.vue'),
    meta: {
      requiresAuth: true,
      resourceKey: 'AdminOrgOverview',
    },
  },
  // User profile page (admin views any user, or self-view)
  {
    path: '/users/:userId',
    name: 'UserProfile',
    component: () => import('@/views/UserProfilePage.vue'),
    props: true,
    meta: {
      requiresAuth: true,
      resourceKey: 'UserManagement',
    },
  },
  {
    path: '/profile',
    name: 'MyProfile',
    component: () => import('@/views/UserProfilePage.vue'),
    meta: {
      requiresAuth: true,
    },
  },
  // Organization profile page
  {
    path: '/orgs/:orgId',
    name: 'OrgProfile',
    component: () => import('@/views/OrgProfilePage.vue'),
    props: true,
    meta: {
      requiresAuth: true,
      resourceKey: 'AdminOrgOverview',
    },
  },
  {
    path: '/requirements-center',
    name: 'RequirementsCenter',
    component: () => import('@/views/admin/RequirementsCenterPage.vue'),
    meta: {
      requiresAuth: true,
      resourceKey: 'RequirementsCenter',
      navLabel: 'Requirements Center',
      inMoreMenu: true,
      category: 'Admin',
      module: 'requirementsCenter',
    },
  },
  {
    path: '/system-monitoring',
    name: 'SystemMonitoring',
    component: () => import('@/views/admin/SystemMonitoringPage.vue'),
    meta: {
      requiresAuth: true,
      resourceKey: 'SystemMonitoring',
      superAdminOnly: true,
      systemControlled: true,
      module: 'systemMonitoring',
      navLabel: 'System Monitoring',
      inMoreMenu: true,
      category: 'Admin',
    },
  },
  {
    path: '/admin/access-control',
    name: 'AccessControl',
    component: () => import('@/views/admin/AccessControlPage.vue'),
    meta: {
      requiresAuth: true,
      resourceKey: 'AccessControl',
      superAdminOnly: true,
      systemControlled: true,
      navLabel: 'Access Control',
      inMoreMenu: true,
      category: 'Admin',
    },
  },
  {
    path: '/admin/suppliers',
    name: 'SupplierManagement',
    component: () => import('@/views/admin/SupplierManagement.vue'),
    meta: {
      requiresAuth: true,
      resourceKey: 'SupplierManagement',
      navLabel: 'Suppliers',
      inMoreMenu: true,
      category: 'Supply Chain',
    },
  },
  {
    path: '/sales-profile',
    name: 'SalesUserProfile',
    component: () => import('@/views/SalesUserProfile.vue'),
    meta: {
      requiresAuth: true,
      resourceKey: 'SalesUserProfile',
    },
  },
  {
    path: '/sales-command-center',
    name: 'SalesCommandCenter',
    component: () => import('@/views/SalesCommandCenter.vue'),
    meta: {
      requiresAuth: true,
      resourceKey: 'SalesCommandCenter',
      navLabel: 'Sales Command Center',
      inMoreMenu: true,
      category: 'People',
    },
  },
  {
    path: '/org-dashboard',
    name: 'OrgDashboard',
    component: () => import('@/views/OrgDashboard.vue'),
    meta: {
      requiresAuth: true,
      resourceKey: 'OrgDashboard',
    },
  },
  {
    path: '/admin-tools',
    name: 'AdminPage',
    component: () => import('@/views/AdminPage.vue'),
    meta: {
      requiresAuth: true,
      resourceKey: 'AdminTools',
      superAdminOnly: true,
      systemControlled: true,
      navLabel: 'Admin Tools',
      inMoreMenu: true,
      category: 'Admin',
    },
  },
  {
    // Phase 90 Stage 2B — standalone test surface for the ported Vue designer (own-map renderer).
    // Super-admin-only dev surface; Stage 4 wires the designer into the real Create/Kiosk flows.
    path: '/designer-v2',
    name: 'DesignerV2',
    component: () => import('@/views/DesignerV2Page.vue'),
    meta: {
      requiresAuth: true,
      resourceKey: 'DesignerV2',
      superAdminOnly: true,
      systemControlled: true,
      navLabel: 'Designer v2',
      inMoreMenu: true,
      category: 'Admin',
    },
  },

  // --- MORE MENU: CONFIGURATION ---
  // Equipment Mgmt is on the access matrix (pages.CostInventory), not super-admin-only, so the warehouse roles can
  // keep stock, prices and purchase orders (bug aNHKYi00EGRuERDlDxGT, Erik 2026-10-26). Every write behind it also
  // needs costCatalog write: the rules, the catalog callables and the inventory writer gate all read that one list.
  {
    path: '/admin-cost-inventory',
    name: 'CostInventoryPage',
    component: () => import('@/views/CostInventoryPage.vue'),
    meta: {
      requiresAuth: true,
      resourceKey: 'CostInventory',
      navLabel: 'Equipment Mgmt',
      inMoreMenu: true,
      category: 'Supply Chain',
    },
  },
  {
    path: '/inventory',
    name: 'InventoryDashboard',
    component: () => import('@/views/InventoryDashboard.vue'),
    meta: {
      requiresAuth: true,
      resourceKey: 'InventoryDashboard',
      navLabel: 'Inventory',
      inMoreMenu: true,
      category: 'Supply Chain',
    },
  },
  // Marketing Hub (New)
  {
    path: '/marketing',
    name: 'MarketingHub',
    component: () => import('@/views/MarketingHub.vue'),
    meta: {
      requiresAuth: true,
      resourceKey: 'MarketingHub',
      navLabel: 'Marketing Hub',
      inMoreMenu: true,
      category: 'Outreach',
      module: 'marketing',
    },
  },
  {
    // The Ad Studio editor (docs/features/ad_studio_integration_FEATURE.md §3.2, Phase 3a). Its own page key, never
    // MarketingHub's (SECURITY_RULES RULE 2.2.1); the adStudio flag is its module, seeded false in the flag store's
    // DEFAULTS, so the route is fail-closed until it is on. The marketing reviewer never reaches it: the isolation
    // guard below sends that account only to MarketingHub. Opened from the hub's Ad Studio tab; without a project id
    // it resumes whatever the editor last had open, as the local app did.
    path: '/marketing/ad-studio/:projectId?',
    name: 'AdStudioEditor',
    component: () => import('@/views/AdStudioPage.vue'),
    meta: {
      requiresAuth: true,
      resourceKey: 'MarketingAdStudio',
      module: 'adStudio',
    },
  },
  {
    // A paid campaign: plan and check (docs/features/paid_campaign_launch_FEATURE.md §3.3, P1). Its own page key, never
    // MarketingHub's; the paidCampaigns flag is its module, seeded false, so the route is fail-closed until it is on.
    // The marketing reviewer never reaches it (the isolation guard below). /marketing/paid-campaigns/new starts a plan.
    path: '/marketing/paid-campaigns/:campaignId?',
    name: 'PaidCampaign',
    component: () => import('@/views/PaidCampaignPage.vue'),
    meta: {
      requiresAuth: true,
      resourceKey: 'MarketingPaidCampaigns',
      module: 'paidCampaigns',
    },
  },
  {
    // Definitive module name is "Automations" (Erik 2026-07-17) — canonical
    // URL /automations; the legacy path stays as an alias so old links work.
    path: '/automations',
    alias: '/notifications-library',
    name: 'NotificationsLibrary',
    component: () => import('@/views/NotificationsLibrary.vue'),
    meta: {
      requiresAuth: true,
      resourceKey: 'NotificationsLibrary',
      navLabel: 'Automations',
      inMoreMenu: true,
      category: 'Outreach',
      module: 'notifications',
    },
  },
  {
    path: '/settings',
    name: 'Settings',
    component: () => import('@/views/Settings.vue'),
    meta: {
      requiresAuth: true,
      resourceKey: 'Settings',
      navLabel: 'Settings',
      inMoreMenu: true,
      category: 'Admin',
    },
  },
  // Phase 77 — AI Reports: fleet's executive briefings (Sunna, Aurora,
  // Vesta, future siblings file structured reports on a configurable
  // cadence; operator reviews + threads back via per-report chat).
  {
    path: '/ai-reports',
    name: 'AiReports',
    component: () => import('@/views/AiReportsPage.vue'),
    meta: {
      requiresAuth: true,
      resourceKey: 'AiReports',
      navLabel: 'AI Reports',
      inMoreMenu: true,
      category: 'Admin',
    },
  },
  // Phase 83 D3.3 — Comm Queue: every AI-initiated email + SMS to customers,
  // sorted by lane priority. The "one place to look" view for fleet comms
  // once AIs are unleashed at scale. Read-only in D3.3a; operator actions
  // (re-enqueue / cancel / pause / force dispatch) ship in D3.3b.
  {
    path: '/comm-queue',
    name: 'CommQueue',
    component: () => import('@/views/CommQueuePage.vue'),
    meta: {
      requiresAuth: true,
      resourceKey: 'CommQueue',
      navLabel: 'Comm Queue',
      inMoreMenu: true,
      category: 'Admin',
    },
  },
  {
    path: '/admin/field-lens',
    name: 'FieldLensConfig',
    component: () => import('@/views/admin/FieldLensConfig.vue'),
    meta: {
      requiresAuth: true,
      resourceKey: 'FieldLensConfig',
      navLabel: 'FieldLens Config',
      inMoreMenu: true,
      category: 'Operations',
    },
  },
  {
    path: '/admin/workflow-builder',
    name: 'WorkflowBuilder',
    component: () => import('@/views/admin/WorkflowBuilder.vue'),
    meta: {
      requiresAuth: true,
      resourceKey: 'WorkflowBuilder',
      superAdminOnly: true,
      systemControlled: true,
      navLabel: 'Workflow Assembler',
      inMoreMenu: true,
      category: 'Admin',
    },
  },
  // Phase 54 v3 — unified Trash & History (replaces the WorkflowBuilder
  // Trash button's modal). Tab 1: stage-task removals (reversible during
  // retention window). Tab 2: soft-deleted master tasks.
  // Reachable only via the Trash button on WorkflowBuilder (router-link).
  // Deliberately no navLabel — HeaderComponent.vue:298 treats any route
  // with `navLabel + !inMoreMenu` as a top-level nav entry; omitting
  // navLabel hides it from the nav entirely.
  // bugReports/51VEmgON — super-admin-only Archived Customers (restore + true
  // erase). Listed in the More menu's ADMIN group, which is safe because
  // HeaderComponent.vue:468 short-circuits `if (meta.superAdminOnly) return
  // isSuperAdmin` before the matrix is consulted — the same gate Admin Tools
  // uses. A non-super-admin never sees the entry, so "limit who can even see
  // that it exists" still holds. Authorisation is enforced server-side in both
  // callables regardless; a hidden nav item is not access control.
  {
    path: '/admin/archived-customers',
    name: 'ArchivedCustomers',
    component: () => import('@/views/admin/ArchivedCustomersPage.vue'),
    meta: {
      requiresAuth: true,
      resourceKey: 'ArchivedCustomers',
      superAdminOnly: true,
      systemControlled: true,
      navLabel: 'Archived Customers',
      inMoreMenu: true,
      category: 'Admin',
    },
  },
  {
    path: '/admin/workflow-trash',
    name: 'WorkflowTrashAndHistory',
    component: () => import('@/views/admin/WorkflowTrashAndHistory.vue'),
    meta: {
      requiresAuth: true,
      resourceKey: 'WorkflowBuilder',
      superAdminOnly: true,
      systemControlled: true,
      category: 'Admin',
    },
  },

  // --- EMAIL TEMPLATES ---
  {
    path: '/email-templates',
    name: 'EmailTemplateBuilder',
    component: () => import('@/views/EmailTemplateBuilder.vue'),
    meta: {
      requiresAuth: true,
      resourceKey: 'EmailTemplateBuilder',
      navLabel: 'Templates',
      inMoreMenu: true,
      category: 'Outreach',
      module: 'emailTemplates',
    },
  },
  {
    path: '/email-templates/:id',
    name: 'EmailTemplateEdit',
    component: () => import('@/views/EmailTemplateBuilder.vue'),
    props: true,
    meta: {
      requiresAuth: true,
      resourceKey: 'EmailTemplateBuilder',
      module: 'emailTemplates',
    },
  },

  // --- SUB-PAGES ---
  {
    path: '/notifications-library/new',
    name: 'NotificationNew',
    component: () => import('@/views/NotificationWizard.vue'),
    meta: { requiresAuth: true, resourceKey: 'NotificationsLibrary', module: 'notifications' },
  },
  {
    path: '/notifications-library/edit/:id',
    name: 'NotificationEdit',
    component: () => import('@/views/NotificationWizard.vue'),
    props: true,
    meta: { requiresAuth: true, resourceKey: 'NotificationsLibrary', module: 'notifications' },
  },
  {
    path: '/admin/workflow-tasks',
    name: 'WorkflowTasks',
    component: () => import('@/views/admin/WorkflowTasks.vue'),
    meta: {
      requiresAuth: true,
      resourceKey: 'WorkflowBuilder',
      superAdminOnly: true,
      systemControlled: true,
    },
  },
  {
    path: '/admin/workflow-stages',
    name: 'WorkflowStages',
    component: () => import('@/views/admin/WorkflowStages.vue'),
    meta: {
      requiresAuth: true,
      resourceKey: 'WorkflowBuilder',
      superAdminOnly: true,
      systemControlled: true,
    },
  },
  // --- MOBILE SIDECAR ---
  // Pared-down, phone-only surface. Staff on a mobile device are auto-routed
  // here after sign-in (see App.vue); a "Full site" link sets a per-device
  // override to reach the full desktop CRM. Reuses the same auth guards.
  //
  // S0d — EACH MOBILE ROUTE MIRRORS ITS DESKTOP EQUIVALENT'S resourceKey
  // (Erik, 2026-08-05: "Mobile routes should mirror the access of the desktop
  // site"). Before this, every mobile route carried only `requiresAuth`, so any
  // authenticated staff member could open /m/comms or /m/customer/:id even when
  // the desktop matrix excluded their role — a permission bypass, not a
  // shortcut. Reusing the DESKTOP key rather than minting `Mobile*` keys is
  // deliberate: two keys for one capability is how they drift apart.
  //
  // ⚠️ MobileFieldLens* has NO desktop equivalent. `/admin/field-lens` is the
  // CONFIG screen (FieldLensConfig, admin+ops); the mobile flow is the CAPTURE
  // surface field crews use. Mapping capture onto the config key is the literal
  // mirror but locks crews out of their own tool — see
  // scripts/verify-mobile-desktop-parity.js for the measured impact and
  // S0D_PROPOSED_MODEL.md for the decision.
  {
    path: '/m',
    component: () => import('@/layouts/MobileLayout.vue'),
    meta: { requiresAuth: true, mobile: true },
    children: [
      { path: '', redirect: '/m/search' },
      {
        path: 'comms',
        name: 'MobileComms',
        component: () => import('@/views/mobile/MobileComms.vue'),
        meta: { requiresAuth: true, mobile: true, resourceKey: 'Communications' },
      },
      {
        path: 'alerts',
        name: 'MobileAlerts',
        component: () => import('@/views/mobile/MobileAlerts.vue'),
        // Was 'Dashboard', which excludes every Field role — see MobileSearch below.
        meta: { requiresAuth: true, mobile: true, resourceKey: 'MobileAlerts' },
      },
      {
        path: 'settings',
        name: 'MobileSettings',
        component: () => import('@/views/mobile/MobileSettings.vue'),
        // Was 'Settings' = ["admin"]. That is correct for the DESKTOP settings
        // surface (the admin config screen) and wrong here: this is a staffer's
        // own preferences. Under the old key no non-admin — Lily, Juan, Jeremy,
        // all three crew — could manage their own notifications on a phone.
        meta: { requiresAuth: true, mobile: true, resourceKey: 'MobileSettings' },
      },
      {
        path: 'settings/notifications',
        name: 'MobileNotificationSettings',
        component: () => import('@/views/mobile/MobileNotificationSettings.vue'),
        meta: { requiresAuth: true, mobile: true, resourceKey: 'MobileSettings' },
      },
      // Notes live in the customer workspace now; redirect old deep links there.
      {
        path: 'notes/:customerId',
        redirect: to => ({ path: `/m/customer/${to.params.customerId}`, query: { tab: 'notes' } }),
      },
      {
        path: 'search',
        name: 'MobileSearch',
        component: () => import('@/views/mobile/MobileSearch.vue'),
        // ⚠️ THIS IS THE MOBILE LANDING PAGE — `/m` redirects here, so this key
        // decides whether a person can open the mobile app AT ALL.
        //
        // It was 'Dashboard'. Evelina's ratified S0h grid deliberately gives Field
        // no DESKTOP Dashboard, which is correct — but reusing that key here turned
        // a desktop policy into "field crew cannot open the mobile app." Christon
        // Lenox, William Stewart and Ian Wright were locked out entirely; Juan
        // Fonseca was spared only because he also holds ops_manager.
        //
        // Reusing desktop keys is the house pattern for mobile and stays the default
        // (see the block comment above). The exception is routes where the mobile
        // meaning genuinely differs from the desktop one — a landing page, personal
        // settings — and those now carry their own Mobile* keys.
        meta: { requiresAuth: true, mobile: true, resourceKey: 'MobileHome' },
      },
      {
        path: 'calendar',
        name: 'MobileCalendar',
        component: () => import('@/views/mobile/MobileCalendar.vue'),
        meta: { requiresAuth: true, mobile: true, resourceKey: 'Calendar' },
      },
      {
        path: 'customer/:id',
        name: 'MobileCustomer',
        component: () => import('@/views/mobile/MobileCustomer.vue'),
        meta: { requiresAuth: true, mobile: true, resourceKey: 'CustomerDetail' },
      },
      {
        path: 'task/:customerId/:instanceId',
        name: 'MobileTask',
        component: () => import('@/views/mobile/MobileTask.vue'),
        meta: { requiresAuth: true, mobile: true, resourceKey: 'CustomerDetail' },
      },
      {
        path: 'fieldlens',
        name: 'MobileFieldLens',
        component: () => import('@/views/mobile/MobileFieldLens.vue'),
        meta: { requiresAuth: true, mobile: true, resourceKey: 'FieldLensCapture' },
      },
      {
        path: 'fieldlens/:customerId',
        name: 'MobileFieldLensBuckets',
        component: () => import('@/views/mobile/MobileFieldLensBuckets.vue'),
        meta: { requiresAuth: true, mobile: true, resourceKey: 'FieldLensCapture' },
      },
      {
        path: 'fieldlens/:customerId/:bucketKey',
        name: 'MobileFieldLensSession',
        component: () => import('@/views/mobile/MobileFieldLensSession.vue'),
        meta: { requiresAuth: true, mobile: true, resourceKey: 'FieldLensCapture' },
      },
      {
        path: 'fieldlens/:customerId/:bucketKey/:reqId',
        name: 'MobileFieldLensRequirement',
        component: () => import('@/views/mobile/MobileFieldLensRequirement.vue'),
        meta: { requiresAuth: true, mobile: true, resourceKey: 'FieldLensCapture' },
      },
    ],
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFoundFallback',
    meta: { public: true },
    redirect: { name: 'ConsumerHome' },
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to) {
    if (to.hash) {
      return { el: to.hash, behavior: 'smooth', top: 80 }
    }
    return { top: 0 }
  },
})

router.beforeEach(async (to, from, next) => {
  // A tab that has learned about a cache-version bump refreshes at the next page change, where a
  // full navigation costs about what the route change costs (src/services/backgroundRefresh.js).
  // When it takes over, the in-app transition is cancelled and the browser loads the target URL.
  if (await maybeRefreshOnNavigation(to, from)) return next(false)

  const userStore = useUserStore()
  const permStore = usePermissionsStore()
  const maintenance = useMaintenanceStore()

  eventBus.emit('loading-start')

  await userStore.waitForInitialization()

  // 1. EARLY EXIT: SignedOut is always reachable while nobody is signed in. With a live session the page would
  // say "You've been signed out" over a running staff session (its listeners, its push toasts), so a signed-in
  // user goes on to step 3 and is sent home like the sign-in page does
  // (docs/bugs/signed_out_page_hosts_live_session_BUG.md). Its profile-unavailable and not-provisioned variants
  // clear the user first, so they still land here.
  if (to.name === 'SignedOut' && !userStore.user) {
    eventBus.emit('loading-stop')
    return next()
  }

  // 1a. ACTIVE CALL NAVIGATION GUARD
  // If the user is on a connected call and tries to leave the current route,
  // prompt them with Cancel / Open in new tab / Continue here. The new-tab
  // path appends ?noVoice=1 so the spawned tab will skip Voice Device init,
  // preserving the original tab's Twilio registration.
  // Only prompt on real route transitions (skip same-name reloads, internal
  // hash changes, and anything not initiated by user navigation).
  try {
    const callStore = useCallStore()
    const sameRoute = from?.name && to?.name && from.name === to.name && from.path === to.path
    if (callStore.myActiveCall && !sameRoute) {
      eventBus.emit('loading-stop')
      const choice = await new Promise(resolve => {
        eventBus.emit('active-call-nav-prompt', {
          destination: to.meta?.navLabel || to.name || to.path,
          resolve,
        })
      })
      if (choice === 'cancel') return next(false)
      if (choice === 'newTab') {
        const href = router.resolve({ ...to, query: { ...to.query, noVoice: '1' } }).href
        try {
          window.open(href, '_blank', 'noopener')
        } catch {
          /* popup blocked */
        }
        return next(false)
      }
      // 'continue' — fall through to normal guard chain
      eventBus.emit('loading-start')
    }
  } catch (e) {
    // Pinia not yet ready (very early routes) — skip guard, never block app boot.
    console.warn('[Router] active-call guard skipped:', e?.message || e)
  }

  const user = userStore.user
  const userRoles = userStore.roles
  const isPublic = to.matched.some(r => r.meta?.public)
  const requiresAuth = to.matched.some(r => r.meta?.requiresAuth)

  // 2. MAINTENANCE MODE
  if (maintenance.enabled && !userStore.isAdmin) {
    if (to.name !== 'Maintenance' && !isPublic) {
      eventBus.emit('loading-stop')
      return next({ name: 'Maintenance' })
    }
  }

  // 3. PUBLIC ROUTES
  if (isPublic) {
    // Redirect signed-in users away from the sign-in and signed-out pages
    if ((to.name === 'SignIn' || to.name === 'SignedOut') && user) {
      eventBus.emit('loading-stop')
      return next({ path: homePathForRoles(userRoles) })
    }
    eventBus.emit('loading-stop')
    return next()
  }

  // 4. AUTH REQUIRED — must be signed in with a role
  //
  // Which sign-in page? A customer bounced off /portal must land on the
  // CONSUMER login, not the internal CRM one (Erik 2026-07-21 — he hit the
  // internal "Sign in with passkey / Continue with Google" page from the portal).
  // Kept route-aware rather than sending everyone to the consumer page: that
  // page is email/password only, so routing staff there would silently strip
  // Google SSO and passkey. Post-login destination is already correct either
  // way — CustomerSignInPage honors ?redirect (default /portal), and the
  // internal page runs handleUserRoleRedirect (staff → /home, customer → /portal).
  const signInRouteFor = target =>
    target.path.startsWith('/portal') || target.name === 'CustomerPortal'
      ? 'CustomerSignIn'
      : 'SignIn'

  if (requiresAuth) {
    if (!user) {
      eventBus.emit('loading-stop')
      return next({ name: signInRouteFor(to), query: { redirect: to.fullPath } })
    }
    if (!user.roles?.userRole) {
      console.warn('[Router] User authenticated but has no userRole.')
      eventBus.emit('loading-stop')
      return next({ name: signInRouteFor(to), query: { redirect: to.fullPath } })
    }
  }

  // 4a. EXTERNAL MARKETING REVIEWER ISOLATION — this check intentionally
  // precedes module flags and the access-control matrix. The reviewer is not
  // allowed to read that internal matrix and may enter only the dedicated
  // synthetic Marketing Hub surface. Public routes remain public.
  if (userStore.isMarketingReviewer) {
    if (to.name !== 'MarketingHub') {
      eventBus.emit('loading-stop')
      return next({ name: 'MarketingHub' })
    }
    eventBus.emit('loading-stop')
    return next()
  }

  // 4b. CUSTOMER ROLE ISOLATION — customer users can only access portal routes
  if (userStore.isCustomer && !userStore.isAdmin && !userStore.isSuperAdmin) {
    const portalRoutes = ['CustomerPortal', 'CustomerPortalPreview', 'SignedOut']
    if (!portalRoutes.includes(to.name) && !to.path.startsWith('/portal')) {
      eventBus.emit('loading-stop')
      return next({ name: 'CustomerPortal' })
    }
  }

  // 4c. MODULE FLAG CHECK — redirect if module is disabled
  if (to.meta?.module) {
    const flagsStore = useFrontendFlagsStore()
    // Await the FIRST flags snapshot before judging: a default-FALSE flag (e.g. solarStudio) would
    // otherwise bounce cold URL loads that beat Firestore — the guard was deciding on the DEFAULTS.
    await flagsStore.whenLoaded()
    if (!flagsStore.isModuleEnabled(to.meta.module)) {
      eventBus.emit('loading-stop')
      return next({ name: 'Dashboard' })
    }
  }

  // 5. SUPER ADMIN ONLY (systemControlled infrastructure routes)
  if (to.matched.some(r => r.meta?.superAdminOnly)) {
    if (!userStore.isSuperAdmin) {
      eventBus.emit('loading-stop')
      return next({ name: 'Unauthorized' })
    }
    // SuperAdmin passes — skip further checks for these routes
    eventBus.emit('loading-stop')
    return next()
  }

  // 6. DATABASE-DRIVEN ACCESS CONTROL (the single source of truth)
  if (to.meta?.resourceKey) {
    await permStore.waitForInit()

    if (!permStore.canAccessPage(to.meta.resourceKey, userRoles, userStore.isSuperAdmin)) {
      console.warn(
        `[AccessControl] Blocked: ${to.name} (key: ${to.meta.resourceKey}, roles: ${userRoles})`
      )
      eventBus.emit('loading-stop')
      return next({ name: 'Unauthorized' })
    }
  }

  // 7. PER-USER OVERRIDES (applied AFTER matrix check passes)
  //    The matrix controls "can this ROLE see the page". These are per-user flags.
  if (to.name === 'Create' && !userStore.canCreateCustomers) {
    eventBus.emit('loading-stop')
    return next({ name: 'Unauthorized' })
  }

  // 8. DATA-LEVEL OWNERSHIP CHECK (CustomerCommandCenter)
  //    The matrix already confirmed the user's ROLE can access CustomerDetail pages.
  //    This additional check verifies the user can see THIS SPECIFIC customer.
  if (to.name === 'CustomerCommandCenter') {
    // SuperAdmin and full-visibility roles skip ownership check (driven by access control matrix)
    if (!permStore.hasFullVisibility('customers', userRoles, userStore.isSuperAdmin)) {
      try {
        const custRef = doc(db, 'customers', to.params.id)
        const snap = await getDoc(custRef)
        if (!snap.exists()) {
          eventBus.emit('loading-stop')
          return next({ name: 'Dashboard' })
        }
        const data = snap.data()
        const orgId = data.system?.organizationId || null
        const submittedByUid = data.contact?.submittedBy?.uid || null
        const isOrgOwner = user.orgRole === 'owner' && user.organizationId === orgId
        // The rep's own customer: system.ownerUids holds uids, so this compared an email to uids and a rep could
        // open only the customers they submitted (customerOwnership.js, ticket EDJq0rxs1xougr9dBm6k).
        const isDirectOwner = isCustomerOwner(data, user.uid) || submittedByUid === user.uid
        if (!isOrgOwner && !isDirectOwner) {
          eventBus.emit('loading-stop')
          return next({ name: 'Unauthorized' })
        }
      } catch (e) {
        console.error('[Router] Customer ownership check failed:', e)
        eventBus.emit('loading-stop')
        return next({ name: 'Dashboard' })
      }
    }
  }

  // 9. ALL CHECKS PASSED
  eventBus.emit('loading-stop')
  return next()
})

// A tab older than the last hosting deploy asks for page chunks the deploy removed; the click used to
// do nothing (docs/bugs/stale_tab_missing_chunk_BUG.md). Recover those by loading the new build at the
// page asked for. Registering any listener silences Vue Router's own logging, so log the rest as it did.
router.onError((error, to) => {
  if (isChunkLoadError(error)) {
    recoverFromMissingChunk(to, error)
    return
  }
  console.error(error)
})

// --- Usage telemetry: page-view beacons (docs/features/USAGE_TELEMETRY_SPEC.md §D) ---
//
// Answers "does any HUMAN actually open this page?" — the question the dead-code audit had to
// infer structurally from nav + access-control config. Rides the existing logFrontendEvent
// callable, so it needs no backend change; classification happens at read time from actor.uid
// (AI fleet accounts carry identity.isAiAgent).
//
// Deliberately cheap: one event per route name per session, fire-and-forget, opt-in by flag,
// and never able to affect navigation.
const loggedPageViews = new Set()

router.afterEach(to => {
  if (to.meta?.public || loggedPageViews.has(to.name)) {
    return
  }

  // Async IIFE: afterEach must not return a promise (Vue Router ignores it, and navigation is
  // already committed), but the flag check has to await the flags snapshot.
  ;(async () => {
    try {
      const userStore = useUserStore()
      if (!userStore.user?.uid) return // logFrontendEvent requires auth

      // Flags arrive via onSnapshot AFTER the first navigation resolves. Without this wait the
      // landing page — usually the most interesting one — is silently never recorded.
      const flagsStore = useFrontendFlagsStore()
      await flagsStore.whenLoaded()
      if (!flagsStore.isFlagOn('usageTelemetryEnabled', false)) return

      // Claimed only once every check has passed, so a page skipped while logged-out still
      // records on a later visit.
      if (loggedPageViews.has(to.name)) return
      loggedPageViews.add(to.name)

      // Dynamic import keeps this off the initial bundle and avoids depending on module-scope
      // Firebase init order at router load.
      const { logEvent } = await import('@/utils/firebase/eventLogger')
      await logEvent({
        eventType: 'PAGE_VIEW',
        action: 'read',
        resource: { type: 'page', id: to.name || to.path },
        context: { metadata: { path: to.path, resourceKey: to.meta?.resourceKey || null } },
      })
    } catch {
      // Telemetry must never surface to the user or affect navigation.
    }
  })()
})

export default router
